import type { AnimationGraph } from "@/document/model/animation";
import { createEmptyAnimationGraph, normalizeAnimationGraph } from "@/document/model/animation";
import { findSceneNode } from "@/document/model/document";
import { editableNodeIds } from "@/document/operations/nodeLocking";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import type { EditorCommandHost } from "../editorCommandHost";

export function editableSelection(nodeIds?: string[]): string[] {
  const selection = selectionStore.getSelection();
  return editableNodeIds(documentStore.getDocument(), nodeIds ?? selection.selectedNodeIds);
}

export function selectNodes(
  host: EditorCommandHost,
  nodeIds: string[],
  primaryNodeId = nodeIds.at(-1),
  options: { recordHistory?: boolean } = {},
): void {
  uiStore.setSelectedPrototypeLinkId(undefined);
  const mode = host.currentSelection().mode;
  const primaryNode = primaryNodeId ? findSceneNode(documentStore.getDocument(), primaryNodeId) : undefined;
  const nextMode = nodeIds.length === 0 || mode === "textEdit"
    || (mode === "vectorEdit" && primaryNode?.type !== "primitive" && primaryNode?.type !== "vectorNetwork")
    ? "select"
    : mode;
  host.updateSelection(host.selectionSnapshot({
    selectedNodeIds: [...new Set(nodeIds)],
    primarySelectedNodeId: primaryNodeId,
    mode: nextMode,
    selectedVertexId: undefined,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  }), options.recordHistory ?? true);
}

export function selectPrototypeLink(host: EditorCommandHost, linkId: string | undefined): void {
  uiStore.setSelectedPrototypeLinkId(linkId);
  if (!linkId) {
    return;
  }

  host.updateSelection(host.selectionSnapshot({
    selectedNodeIds: [],
    primarySelectedNodeId: undefined,
    mode: "select",
    selectedVertexId: undefined,
    selectedVertexIds: [],
    selectedEdgeIds: [],
    selectedHandle: undefined,
  }), true);
  uiStore.setMessage("Selected prototype link");
}

export function selectVectorVertex(host: EditorCommandHost, vertexId: string | undefined): void {
  host.updateSelection(host.selectionSnapshot({
    selectedVertexId: vertexId,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  }), true);
}

export function selectVectorVertices(host: EditorCommandHost, vertexIds: string[]): void {
  host.updateSelection(host.selectionSnapshot({
    selectedVertexId: vertexIds.at(-1),
    selectedVertexIds: vertexIds,
    selectedEdgeIds: [],
    selectedHandle: undefined,
  }), true);
}

export function selectVectorHandle(
  host: EditorCommandHost,
  handle: { edgeId: string; endpoint: "from" | "to" } | undefined,
): void {
  host.updateSelection(host.selectionSnapshot({
    selectedHandle: handle,
    selectedVertexId: undefined,
    selectedEdgeIds: [],
  }), true);
}

export function selectVectorEdges(host: EditorCommandHost, edgeIds: string[]): void {
  host.updateSelection(host.selectionSnapshot({
    selectedEdgeIds: edgeIds,
    selectedVertexId: undefined,
    selectedHandle: undefined,
  }), true);
}

export function findAnimationRootForNode(graph: AnimationGraph, nodeId: string | undefined): string | undefined {
  if (!nodeId) {
    return undefined;
  }
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) {
      continue;
    }
    visited.add(current);
    const node = graph.nodes.find(n => n.id === current);
    if (!node) {
      continue;
    }
    if (node.kind === "animation") {
      return node.id;
    }
    for (const connection of graph.connections) {
      if (connection.from.nodeId === current && !visited.has(connection.to.nodeId)) {
        queue.push(connection.to.nodeId);
      }
      if (connection.to.nodeId === current && !visited.has(connection.from.nodeId)) {
        queue.push(connection.from.nodeId);
      }
    }
  }
  return undefined;
}

export function activateAnimationRoot(rootId: string | undefined, activeRootId?: string): void {
  const current = activeRootId ?? uiStore.animation.getValue().activeRootId;
  if (rootId === current) {
    return;
  }
  uiStore.patchAnimation({
    activeRootId: rootId,
    playing: false,
    timeMs: 0,
    loopRegion: undefined,
  });
}

export function activateAnimationRootForNode(
  nodeId: string | undefined,
  graph = normalizeAnimationGraph(documentStore.getDocument().animation ?? createEmptyAnimationGraph()),
): void {
  if (!nodeId) {
    activateAnimationRoot(undefined);
    return;
  }
  activateAnimationRoot(findAnimationRootForNode(graph, nodeId));
}

export function selectAnimationNodes(host: EditorCommandHost, nodeIds: string[], primaryNodeId?: string): void {
  selectionStore.setSelectedObjectConnectionKeys([]);
  activateAnimationRootForNode(primaryNodeId ?? nodeIds[0]);
  host.updateSelection(host.selectionSnapshot({
    selectedAnimationNodeIds: nodeIds,
    primarySelectedAnimationNodeId: primaryNodeId,
    selectedAnimationConnectionIds: [],
  }), true);
}

export function selectAnimationConnections(host: EditorCommandHost, connectionIds: string[]): void {
  selectionStore.setSelectedObjectConnectionKeys([]);
  host.updateSelection(host.selectionSnapshot({
    ...(connectionIds.length > 0 ? { selectedAnimationNodeIds: [], primarySelectedAnimationNodeId: undefined } : {}),
    selectedAnimationConnectionIds: connectionIds,
  }), true);
}

export function selectObjectConnections(keys: string[]): void {
  selectionStore.setSelectedObjectConnectionKeys(keys);
  if (keys.length > 0) {
    selectionStore.patchSelection({ selectedAnimationConnectionIds: [] });
    selectionStore.patchSelection({ selectedAnimationNodeIds: [] });
    selectionStore.patchSelection({ primarySelectedAnimationNodeId: undefined });
  }
}
